// Ported from mapTile.utils.js on 2026-10-07.
const CARTO_BASEMAPS_API_KEY = process.env.PUBLIC_CARTO_BASEMAPS_API_KEY || '';
const cartoTileUrl = (path: string): string =>
  `https://{s}.basemaps.cartocdn.com/${path}/{z}/{x}/{y}{r}.png${
    CARTO_BASEMAPS_API_KEY ? `?key=${CARTO_BASEMAPS_API_KEY}` : ''
  }`;

export const STREET_TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
export const STREET_TILE_ATTRIBUTION =
  '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors';
export const CARTO_TILE_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>';
export const VOYAGER_TILE_URL = cartoTileUrl('rastertiles/voyager');
export const DARK_TILE_URL = cartoTileUrl('dark_all');

export const MAP_STYLE_OPTIONS = [
  { value: 'default', label: 'Default' },
  { value: 'monochrome', label: 'Monochrome' },
  { value: 'enhancedContrast', label: 'Enhanced Contrast' },
  { value: 'highContrast', label: 'High Contrast' },
  { value: 'defaultDark', label: 'Default (Dark)' },
  { value: 'voyager', label: 'Voyager' },
  { value: 'voyagerDark', label: 'Voyager (Dark)' },
  { value: 'dark', label: 'Dark' },
] as const;

export type MapStyle = (typeof MAP_STYLE_OPTIONS)[number]['value'];

export const DEFAULT_MAP_OPTIONS = {
  darkMode: false,
  mapStyle: 'default',
} as const;

type MapOptions = {
  darkMode?: unknown;
  mapStyle?: unknown;
};

type TileStyleConfig = {
  url: string;
  attribution: string;
  className: string | undefined;
  darkMode: boolean;
};

const TILE_STYLE_CONFIG: Record<MapStyle, TileStyleConfig> = {
  default: {
    url: STREET_TILE_URL,
    attribution: STREET_TILE_ATTRIBUTION,
    className: undefined,
    darkMode: false,
  },
  monochrome: {
    url: STREET_TILE_URL,
    attribution: STREET_TILE_ATTRIBUTION,
    className: 'fusion-map-monochrome-tiles',
    darkMode: true,
  },
  enhancedContrast: {
    url: VOYAGER_TILE_URL,
    attribution: CARTO_TILE_ATTRIBUTION,
    className: undefined,
    darkMode: false,
  },
  highContrast: {
    url: VOYAGER_TILE_URL,
    attribution: CARTO_TILE_ATTRIBUTION,
    className: undefined,
    darkMode: false,
  },
  voyager: {
    url: VOYAGER_TILE_URL,
    attribution: CARTO_TILE_ATTRIBUTION,
    className: undefined,
    darkMode: false,
  },
  voyagerDark: {
    url: VOYAGER_TILE_URL,
    attribution: CARTO_TILE_ATTRIBUTION,
    className: 'fusion-map-dark-tiles',
    darkMode: true,
  },
  defaultDark: {
    url: STREET_TILE_URL,
    attribution: STREET_TILE_ATTRIBUTION,
    className: 'fusion-map-dark-tiles',
    darkMode: true,
  },
  dark: {
    url: DARK_TILE_URL,
    attribution: CARTO_TILE_ATTRIBUTION,
    className: undefined,
    darkMode: true,
  },
};

const isMapOptions = (value: unknown): value is MapOptions =>
  typeof value === 'object' && value !== null;

const isMapStyle = (value: unknown): value is MapStyle =>
  typeof value === 'string' && Object.prototype.hasOwnProperty.call(TILE_STYLE_CONFIG, value);

/**
 * Normalizes stored map options and legacy boolean dark-mode values.
 * @param mapOptionsOrDarkMode - Persisted map options or a legacy dark-mode boolean.
 * @returns Valid map options with the matching tile style.
 */
export const normalizeMapOptions = (
  mapOptionsOrDarkMode: unknown = DEFAULT_MAP_OPTIONS,
): { darkMode: boolean; mapStyle: MapStyle } => {
  if (typeof mapOptionsOrDarkMode === 'boolean') {
    return {
      ...DEFAULT_MAP_OPTIONS,
      darkMode: mapOptionsOrDarkMode,
      mapStyle: mapOptionsOrDarkMode ? 'voyagerDark' : 'default',
    };
  }

  if (!isMapOptions(mapOptionsOrDarkMode)) {
    return { ...DEFAULT_MAP_OPTIONS };
  }

  const fallbackStyle = mapOptionsOrDarkMode.darkMode
    ? 'voyagerDark'
    : DEFAULT_MAP_OPTIONS.mapStyle;
  const mapStyle = isMapStyle(mapOptionsOrDarkMode.mapStyle)
    ? mapOptionsOrDarkMode.mapStyle
    : fallbackStyle;

  return {
    ...DEFAULT_MAP_OPTIONS,
    mapStyle,
    darkMode: TILE_STYLE_CONFIG[mapStyle].darkMode,
  };
};

/**
 * Returns tile-layer settings for the selected map style.
 * @param mapOptionsOrDarkMode - Persisted map options or a legacy dark-mode boolean.
 * @returns Tile URL, attribution, and display settings.
 */
export const getStreetTileLayerConfig = (mapOptionsOrDarkMode: unknown = false) => {
  const normalizedMapOptions = normalizeMapOptions(mapOptionsOrDarkMode);
  const styleConfig = TILE_STYLE_CONFIG[normalizedMapOptions.mapStyle] || TILE_STYLE_CONFIG.default;

  return {
    ...styleConfig,
    mapStyle: normalizedMapOptions.mapStyle,
    containerClassName: styleConfig.darkMode ? 'fusion-map-dark-ui' : undefined,
  };
};
